import 'swiper/css'

import { useScrollbar } from '@14islands/r3f-scroll-rig'
import { AnimatePresence, motion } from 'framer-motion'
import type { GetStaticProps, InferGetStaticPropsType } from 'next'
import Head from 'next/head'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'

import {
  ECOSYSTEM_SECTIONS,
  EcosystemSection,
  EXPO_OUT,
  PAGE_TITLE_PREFIX,
} from '~/components/constants'
import BasicCard from '~/components/ecosystem/BasicCard'
import OutlineButton from '~/components/ecosystem/OutlineButton'
import { Column, Grid } from '~/components/Grid'
import Layout from '~/components/Layout'
import { readToken } from '~/lib/sanity.api'
import { getClient } from '~/lib/sanity.client'
import {
  Category,
  Deployed,
  getDeployedWithCategories,
  getNavigation,
  getProvidersWithCategories,
  getShowcaseWithCategories,
  getToolsWithCategories,
  Navigation,
  Providers,
  Showcase,
  Tools,
} from '~/lib/sanity.queries'
import type { SharedPageProps } from '~/pages/_app'

import Button from '../components/Button'
import ProvidersCard from '../components/ecosystem/ProvidersCard'
import Cross from '../components/icons/Cross'
import { FilterIcon } from '../components/icons/FilterIcon'
import { SearchIcon } from '../components/icons/SearchIcon'
import Card from '~/components/Card'

export const getStaticProps: GetStaticProps<
  SharedPageProps & {
    navigation: Navigation
    toolsWithCategories: { items: Tools[]; categories: Category[] }
    showcaseWithCategories: { items: Showcase[]; categories: Category[] }
    deployedWithCategories: { items: Deployed[]; categories: Category[] }
    providersWithCategories: { items: Providers[]; categories: Category[] }
  }
> = async ({ draftMode = false }) => {
  const client = getClient(draftMode ? { token: readToken } : undefined)
  const navigation = await getNavigation(client)
  const toolsWithCategories = await getToolsWithCategories(client)
  const showcaseWithCategories = await getShowcaseWithCategories(client)
  const deployedWithCategories = await getDeployedWithCategories(client)
  const providersWithCategories = await getProvidersWithCategories(client)

  return {
    props: {
      draftMode,
      token: draftMode ? readToken : '',
      navigation,
      toolsWithCategories,
      showcaseWithCategories,
      deployedWithCategories,
      providersWithCategories,
    },
  }
}

const OPACITY_CLASSES = ['opacity-0 duration-50', 'opacity-100 duration-200']

function useFetchSectionData(
  sectionKey,
  fetchFunction,
  setFunction,
  isLoadingSetter,
  props,
) {
  const { draftMode } = props
  return useCallback(async () => {
    isLoadingSetter(true)
    const client = getClient(draftMode ? { token: readToken } : undefined)
    const data = await fetchFunction(client)
    setFunction(data)
    isLoadingSetter(false)
  }, [draftMode, fetchFunction, isLoadingSetter, setFunction])
}

export default function EcosystemPage(
  props: InferGetStaticPropsType<typeof getStaticProps>,
) {
  const navigation = props.navigation
  const [toolsWithCategories, setToolsWithCategories] = useState<{
    items: Tools[]
    categories: Category[]
  } | null>(props.toolsWithCategories)
  const [showcaseWithCategories, setShowcaseWithCategories] = useState<{
    items: Showcase[]
    categories: Category[]
  } | null>(props.showcaseWithCategories)
  const [deployedWithCategories, setDeployedWithCategories] = useState<{
    items: Deployed[]
    categories: Category[]
  } | null>(props.deployedWithCategories)
  const [providersWithCategories, setProvidersWithCategories] = useState<{
    items: Providers[]
    categories: Category[]
  } | null>(props.providersWithCategories)

  const [isLoadingTools, setIsLoadingTools] = useState(false)
  const [isLoadingShowcase, setIsLoadingShowcase] = useState(false)
  const [isLoadingDeployed, setIsLoadingDeployed] = useState(false)
  const [isLoadingProviders, setIsLoadingProviders] = useState(false)

  const sections = useMemo(
    () => ({
      Showcase: showcaseWithCategories,
      Tools: toolsWithCategories,
      Deployed: deployedWithCategories,
      Providers: providersWithCategories,
    }),
    [
      showcaseWithCategories,
      toolsWithCategories,
      deployedWithCategories,
      providersWithCategories,
    ],
  )

  const [activeSection, setActiveSection] = useState<EcosystemSection | null>(
    ECOSYSTEM_SECTIONS[0],
  )

  const fetchToolsWithCategories = useFetchSectionData(
    'Tools',
    getToolsWithCategories,
    setToolsWithCategories,
    setIsLoadingTools,
    props,
  )
  const fetchShowcaseWithCategories = useFetchSectionData(
    'Showcase',
    getShowcaseWithCategories,
    setShowcaseWithCategories,
    setIsLoadingShowcase,
    props,
  )
  const fetchDeployedWithCategories = useFetchSectionData(
    'Deployed',
    getDeployedWithCategories,
    setDeployedWithCategories,
    setIsLoadingDeployed,
    props,
  )
  const fetchProvidersWithCategories = useFetchSectionData(
    'Providers',
    getProvidersWithCategories,
    setProvidersWithCategories,
    setIsLoadingProviders,
    props,
  )

  useEffect(() => {
    const fetchFunctions = {
      Tools: fetchToolsWithCategories,
      Showcase: fetchShowcaseWithCategories,
      Deployed: fetchDeployedWithCategories,
      Providers: fetchProvidersWithCategories,
    }
    const currentFetchFunction = fetchFunctions[activeSection]
    if (currentFetchFunction && !sections[activeSection]) {
      currentFetchFunction()
    }
  }, [
    activeSection,
    fetchDeployedWithCategories,
    fetchProvidersWithCategories,
    fetchShowcaseWithCategories,
    fetchToolsWithCategories,
    sections,
  ])

  const categoriesToRender = sections[activeSection]?.categories ?? []

  const onSectionClick = (section: EcosystemSection) => {
    setActiveSection(section)
    setSelectedCategories([])
    setSearchQuery('')
  }
  const [selectedCategories, setSelectedCategories] = useState<Category[]>([])

  const toggleCategory = (category: Category) => {
    setSelectedCategories((current) => {
      const index = current.findIndex((c) => c._id === category._id)
      if (index > -1) return current.filter((_, i) => i !== index)
      return [...current, category]
    })
  }

  const onCategoryClick = (category: Category) => {
    toggleCategory(category)
  }

  const clearCategories = () => {
    setSelectedCategories([])
  }

  const isActiveCategory = (category: Category) =>
    selectedCategories.includes(category)

  const [cardsToRender, setCardsToRender] = useState<
    Tools[] | Showcase[] | Deployed[] | Providers[]
  >([])

  const [scrollOpacity, setScrollOpacity] = useState('opacity-0')
  const [scrollProgress, setScrollProgress] = useState(0)

  const onSwiperScroll = (event) => {
    setScrollProgress(event.progress)
    if (event.progress === 0) {
      setScrollOpacity('opacity-0')
    } else {
      setScrollOpacity('opacity-100')
    }
  }

  const [searchQuery, setSearchQuery] = useState('')
  const searchQueryLower = searchQuery.toLowerCase()

  useEffect(() => {
    const filteredItems =
      sections[activeSection]?.items.filter((item) => {
        const matchesCategories = selectedCategories.every((category) =>
          item.categories.some(
            (itemCategory) => itemCategory._id === category._id,
          ),
        )

        const matchesSearch =
          item._type === 'providers'
            ? item.name.toLowerCase().includes(searchQueryLower) ||
              item.region.toLowerCase().includes(searchQueryLower) ||
              item.status.toLowerCase().includes(searchQueryLower) ||
              item.gpu.toString().includes(searchQueryLower) ||
              item.gpuMax.toString().includes(searchQueryLower) ||
              item.memory.toLowerCase().includes(searchQueryLower) ||
              item.memoryMax.toLowerCase().includes(searchQueryLower) ||
              item.cpu.toString().includes(searchQueryLower) ||
              item.cpuMax.toString().includes(searchQueryLower) ||
              item.activeLeases.toString().includes(searchQueryLower)
            : item.title.toLowerCase().includes(searchQueryLower) ||
              item.body.toLowerCase().includes(searchQueryLower)

        return matchesCategories && matchesSearch
      }) ?? []

    setCardsToRender(
      filteredItems as Tools[] | Showcase[] | Deployed[] | Providers[],
    )
    setScrollProgress(0)
    setScrollOpacity('opacity-0')
  }, [activeSection, selectedCategories, sections, searchQueryLower])

  // const isProviders = activeSection === 'Providers'

  const [openFiltersDrawer, setOpenFiltersDrawer] = useState(false)
  const [expandSearch, setExpandSearch] = useState(false)
  const scroll = useScrollbar()

  const closeFilters = () => {
    setOpenFiltersDrawer(false)
  }

  const toggleFilters = () => {
    setOpenFiltersDrawer((prev) => !prev)
    if (openFiltersDrawer) {
      scroll.__lenis.start()
    } else {
      scroll.__lenis.stop()
    }
  }

  const pageTile = `${PAGE_TITLE_PREFIX} Ecosystem`

  return (
    <>
      <Head>
        <title>{pageTile}</title>
      </Head>
      <Layout navigation={navigation}>
        <div className="relative">
          <div
            className={`sticky top-0 xl:hidden
                pt-header md:pt-header-lg mt-4 pb-4 z-10
                bg-black border-b border-dark-border
              `}
          >
            <div
              className={`filter-grid 
                ${expandSearch ? 'is-expanded' : ''}
              `}
            >
              {openFiltersDrawer && (
                <div
                  className="absolute left-0 -bottom-px z-10 bg-black w-full transform translate-y-full
            h-[70vh] xl:h-[80vh] xl:translate-y-0 xl:top-0 xl:bottom-auto xl:w-80
            px-4 md:px-16 pt-4
          "
                >
                  <ul className="flex flex-row items-start justify-start gap-3 flex-wrap">
                    <li
                      className="body-small overflow-hidden"
                      key={`All${activeSection}`}
                    >
                      <span className="block">
                        <OutlineButton
                          isActive={selectedCategories.length === 0}
                          onClick={clearCategories}
                          variant="white"
                        >
                          All
                        </OutlineButton>
                      </span>
                    </li>
                    {categoriesToRender.map((category) => (
                      <li
                        className="body-small overflow-hidden"
                        key={`${category.name}${activeSection}`}
                      >
                        <span className="block text-off-white">
                          <OutlineButton
                            isActive={isActiveCategory(category)}
                            onClick={() => onCategoryClick(category)}
                            variant="white"
                          >
                            <span className="flex flex-row items-center justify-center gap-3">
                              {category.name}
                              {isActiveCategory(category) && (
                                <Cross className="transform rotate-45 w-3 h-3 z-10" />
                              )}
                            </span>
                          </OutlineButton>
                        </span>
                      </li>
                    ))}
                    <li>
                      <Button onClick={closeFilters} hasArrow={false}>
                        Apply changes
                      </Button>
                    </li>
                  </ul>
                </div>
              )}
              <Column
                className={`flex justify-between items-between ${expandSearch ? 'w-0 overflow-hidden opacity-0' : ''}`}
              >
                <button onClick={toggleFilters} className="mono-large z-20">
                  <span className="flex flex-row items-center justify-center gap-3">
                    Filter <FilterIcon></FilterIcon>
                  </span>
                </button>
              </Column>
              <Column className="flex justify-end">
                <div
                  className={`rounded-full h-10 md:h-12 bg-transparent border border-off-white text-off-white relative
                    w-full transition-all duration-300 ease-out-expo min-w-40
                `}
                >
                  <SearchIcon className="absolute right-0 top-1/2 transform -translate-y-1/2 w-4 h-4 mr-4 md:mr-8 text-off-white"></SearchIcon>
                  <input
                    className="bg-transparent unset-all w-full h-full pl-4 md:pl-8 pr-8 sm:pr-14 rounded-full mono-large"
                    placeholder={`Search ${activeSection}`}
                    onFocus={() => setExpandSearch(true)}
                    onBlur={() => setExpandSearch(false)}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  ></input>
                </div>
              </Column>
            </div>
          </div>

          <div className="xl:pt-header-lg relative mb-20 min-h-screen z-0">
            <Grid>
              <Column className="col-span-12 xl:col-span-10 xl:col-start-2">
                <div className="flex flex-col items-start justify-center z-10 w-full mt-4 md:mt-20">
                  <div className="text-left xl:text-center w-full">
                    <h1 className="h1 text-gradient m-auto text-pretty mb-4 xl:mb-20">
                      Ecosystem
                    </h1>
                  </div>
                </div>
              </Column>
            </Grid>
            <div className="grid grid-cols-12 gap-3 w-full max-w-1920 m-auto px-0 md:px-16">
              <Column className="col-span-12 w-full flex">
                <div className="flex flex-row items-start justify-between w-full xl:border-b border-dark-border mb-4 xl:pb-9 xl:mb-20">
                  <div
                    className="flex flex-row items-center justify-start w-full flex-grow gap-4 flex-shrink-1 md:flex-wrap
                  hide-scrollbar scroll-smooth overflow-auto
                  px-4 md:px-0 pb-4 xl:pb-0
                "
                  >
                    {ECOSYSTEM_SECTIONS.map((section) => (
                      <OutlineButton
                        isActive={activeSection === section}
                        key={section}
                        onClick={() => onSectionClick(section)}
                      >
                        {section}
                      </OutlineButton>
                    ))}
                  </div>
                  <div className="flex-row items-center justify-end hidden xl:flex">
                    <div
                      className={`rounded-full h-10 md:h-12 bg-transparent border border-off-white text-off-white relative
                     w-40 focus-within:w-60 xl:focus-within:w-96 transition-all duration-300 ease-out-expo
                  `}
                    >
                      <SearchIcon className="absolute right-0 top-1/2 transform -translate-y-1/2 w-4 h-4 mr-4 md:mr-8 text-off-white"></SearchIcon>
                      <input
                        className="bg-transparent unset-all w-full h-full pl-4 md:pl-8 pr-14 rounded-full mono-large"
                        placeholder="Search"
                        onChange={(e) => setSearchQuery(e.target.value)}
                      ></input>
                    </div>
                  </div>
                </div>
              </Column>
            </div>

            <div className="grid-padding-left w-full overflow-hidden">
              <div className="grid grid-cols-12 gap-3 w-full m-auto md:pl-14">
                <Column
                  className="
                  hidden
                  xl:block
                  lg:col-span-3 
                  xl:col-span-2
                "
                >
                  <div className="flex flex-row items-start justify-between w-full border-b border-dark-border">
                    <div className="flex flex-col items-start justify-start">
                      <h2 className="body-small text-grey m-auto text-pretty mb-3">
                        Categories
                      </h2>
                    </div>
                  </div>
                  <div className="flex flex-col items-start justify-start w-full pt-3">
                    <AnimatePresence mode="popLayout">
                      <motion.ul
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.5, ease: EXPO_OUT }}
                        layout
                        layoutId={`All${activeSection}`}
                        className="flex flex-col items-start justify-start gap-3"
                      >
                        <li
                          className="body-small overflow-hidden"
                          key={`All${activeSection}`}
                        >
                          <span className="block translate-y-8 animate-category-intro">
                            <button
                              className={`transition duration-300 ${selectedCategories.length === 0 ? 'text-green' : 'text-off-white hover:text-green'}`}
                              onClick={clearCategories}
                            >
                              All
                            </button>
                          </span>
                        </li>
                        {categoriesToRender.map((category) => (
                          <li
                            className="body-small overflow-hidden"
                            key={`${category.name}${activeSection}`}
                          >
                            <span className="block translate-y-8 animate-category-intro">
                              <button
                                className={`text-left transition duration-300 ${isActiveCategory(category) ? 'text-green' : 'text-off-white hover:text-green'}`}
                                onClick={() => onCategoryClick(category)}
                              >
                                {category.name}
                              </button>
                            </span>
                          </li>
                        ))}
                      </motion.ul>
                    </AnimatePresence>
                  </div>
                </Column>
                <Column
                  className="
                  col-span-12
                  xl:col-span-9 xl:col-start-4
                "
                >
                  <div className="flex items-center mb-8 relative grid-padding-right pl-4 md:pl-0">
                    <h2 className="h4 overflow-hidden h-14 relative w-80 z-10 hidden xl:block">
                      <AnimatePresence>
                        {ECOSYSTEM_SECTIONS.map((section) => {
                          if (section === activeSection) {
                            return (
                              <motion.div
                                key={activeSection}
                                id={activeSection}
                                initial={{ y: '140%' }}
                                animate={{ y: 0 }}
                                exit={{ y: '-140%' }}
                                transition={{ duration: 1.5, ease: EXPO_OUT }}
                                className="absolute top-0 left-0 h-14 w-full overflow-hidden origin-left"
                              >
                                <span
                                  className="block animate-fade-up-in"
                                  key={activeSection}
                                >
                                  <span className="text-gradient block pb-2">
                                    {activeSection}
                                  </span>
                                </span>
                              </motion.div>
                            )
                          }
                        })}
                      </AnimatePresence>
                    </h2>
                    <div
                      className={`w-full xl:w-56 h-[4px] rounded-full bg-grey-25 pr-20 xl:pr-6 relative
                    transition duration-700 ${'opacity-100'}
                  `}
                    >
                      <div className="relative w-full h-full">
                        <div
                          style={{
                            transform: `translateX(${Math.min(scrollProgress * 150, 100)}%)`,
                          }}
                          className="absolute top-0 left-0 h-full w-full transition-all duration-700 ease-out-expo"
                        >
                          <div className="w-20 xl:w-6 h-full bg-green rounded-full"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="z-10 relative flex overflow-hidden w-full pl-4 sm:pl-0 ">
                    <div
                      className={`absolute flex-shrink-0 top-0 left-0 w-14 z-20 h-full transition left-fade-out ${scrollOpacity}`}
                    ></div>
                    {/* {isProviders ? (
                      <div
                        className={`
                      z-10 flex flex-shrink-0 gap-6 snap-start items-start justify-start w-full pb-4 pr-4 md:pr-14
                      pl-4 md:pl-0 flex-wrap `}
                      >
                        {cardsToRender.map((item, index) =>
                          item._type === 'providers' ? (
                            <ProvidersCard key={index} content={item} />
                          ) : null,
                        )}
                      </div>
                    ) : ( */}
                      <div className="flex flex-wrap gap-6">
                        {cardsToRender.map((item, index) =>
                          item._type === 'providers' ? null : (
                            <BasicCard
                              key={item._id + selectedCategories.length}
                              content={item}
                            />
                          ),
                        )}
                      </div>
                    {/* )} */}
                    {activeSection === 'AI Agents' && (
                      <div className="flex gap-6 w-full">
                        <div className="flex flex-col gap-4">
                          <h3 className="h5 font-bold text-gradient">
                            AI Agents Coming Soon
                          </h3>
                          <p className="text-white max-w-md">
                            We&apos;re working on bringing intelligent AI agents
                            to our ecosystem. Stay tuned for autonomous agents
                            that will revolutionize how you work.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </Column>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  )
}
